import { IGenre } from 'services/enums/types';
import { ISettlementInfoContractForm } from 'services/music-product-digital/types/form';
import { ISettlementInfoContractMutation } from 'services/music-product-digital/types/mutation';
import { IStatisticsQuery } from 'services/statistics/types';
import {
  IArtistWithRoleForm,
  IArtistWithRoleMutation,
  ICopyrights,
  IExternalId,
  IInfinitePageParams,
  IInfinitePageResponse,
  ITitle,
  IVersion,
  IWaitingForFile,
  STATUS,
} from 'services/types';

export interface ITrackFilePath {
  wavLink?: string;
  mp3PreviewLink?: string;
  originalFileLink?: string;
  mp3Link?: string;
  flacLink?: string;
  licenceFilePath?: string | null;
  id?: string;
}

export interface ITrackArtistMutation extends IArtistWithRoleMutation {}

export interface ITrackArtistMutationForm extends IArtistWithRoleForm {
  isDuplicated: boolean;
}

export interface ITrackBase extends IVersion {
  text: string | null;
  isrc: string;
  pline: ICopyrights | null;
  cline: ICopyrights | null;
  language: number;

  isCreatedWithAi: boolean;
  externalIds: IExternalId[];
  // source: TRACK_SOURCE;
  otherTitles: ITitle[];
  mainTitle: ITitle;
}

export interface ITrackMutation extends ITrackBase {
  status: STATUS;
  isInstrumental: boolean;
  artists: ITrackArtistMutation[];
  contractRequests: ISettlementInfoContractMutation[];
  genre: number | null;
  censorious: TRACK_CENSORIOUS | null;
}

export interface ITrackMutationForm
  extends
    Omit<
      ITrackMutation,
      'artists' | 'contractRequests' | 'genre' | 'censorious'
    >,
    ITrackFilePath {
  file?: File;
  genre: IGenre;
  censorious: TRACK_CENSORIOUS;
  artists: ITrackArtistMutationForm[];
  contracts: ISettlementInfoContractForm[];
  statistics: IStatisticsQuery;
  hasDuplicatedArtist: boolean | null;
  id: string;
  hasMatchFingerprint: boolean;
  matchFingerprintScore: number | null;
  waitingForAcoustIdSubmission: boolean;
}

export interface ITrackId {
  trackId: string;
}

export interface ICreateTrack extends ITrackMutation, IWaitingForFile {}

export interface IUpdateTrack extends ITrackMutation, ITrackId {}

export interface ITrackArtistResponse extends ITrackArtistMutationForm {
  rolePlacement: number;
  artistStatus?: STATUS;
  pictureId?: string;
  picturePath?: string;
  pictureExtension?: string;
  previewPicture?: string;
  missingFullNameForRole?: boolean;
}

export interface ITrackResponse extends ITrackBase, ITrackFilePath {
  id: string;
  genre: IGenre | null;
  censorious: TRACK_CENSORIOUS | null;
  trackStatus: STATUS;
  instrumental: boolean;
  artists: ITrackArtistResponse[];
  contracts: ISettlementInfoContractForm[];
  hasDuplicatedArtist: boolean | null;
  source?: TRACK_SOURCE | null;
  canRemoveContracts?: boolean;
  canUpdateISRC?: boolean;

  hasMatchFingerprint: boolean;
  matchFingerprintScore: number | null;
  waitingForAcoustIdSubmission?: boolean;
}

export interface ITracksResponse extends ITrackResponse {}

export interface ITrackPageResponse extends IInfinitePageResponse<ITracksResponse> {}

export interface ITrackFilteredListParams extends IInfinitePageParams {
  title?: string;
  isrc?: string;
  ean?: string;
  artistNickname?: string;
  artistName?: string;
  hasDuplicatedArtist?: boolean;
}

export interface ITrackLight {
  id: string;
  mainTitle: string;
  isrc: string;
}

export interface ITrackContractsTime {
  id: string;
  contracts: ISettlementInfoContractForm[];
}

export interface ITrackFileUploadComplete extends IVersion {
  file: File;
  trackId: string;
}

export interface ITrackFingerprintSubmission {
  id: string;
  status: {
    id: TRACK_FINGERPRINT_SUBMISSION_STATUS;
  };
  isActive: boolean;
  acoustId: string;
  createdAt: string;
}

export interface ITrackFingerprintLookup {
  match: boolean;
  score: number;
  selfScore: number | null;
  recordings: unknown[] | null;
}

export enum TRACK_CENSORIOUS {
  unknown = -1,
  explicit = 1,
  notExplicit = 2,
  clean = 3,
}

export enum TRACK_SOURCE {
  unknown = -1,
  local = 1,
  fromDistribution = 2,
}

export enum TRACK_FINGERPRINT_SUBMISSION_STATUS {
  waitingToSubmit = 0,
  pending = 1,
  imported = 2,
  duplicate = 3,
  rejected = 4,
  disabled = 5,
  deleted = 6,
  error = 7,
  notEligibleForSubmit = 8,
}
